import { I18n } from "@lingui/core";
// import moment from 'moment'
import { t } from "@lingui/macro";
import { ViewBy, RankDir, Zones } from "../../../context/reports/comfort/types";
import { CompareTo, TimeFrame } from "../../../context/reports/types";

/**
 * Calculates the chart height necessary to produce a 2 group bar chart
 * with bars of 32px high and 16px gap between bar groups
 *
 * @param count number of bar groups
 * @param spacing additional space for external chart elements, such as legend and caption
 */
export function getChartSizes(count: number, spacing = 134, groupSize = 2) {
  // * Each group consists of 2 bars
  const barCount = count < 10 ? count * groupSize : 10 * groupSize;
  // * Each bar is 32px high
  const barHeight = 32;
  // * gap between bar groups is a percentage of a single bar's height (25% of 32px = 8px)
  const gap = barHeight * 0.25;
  // * Add it all together to calculate the height needed for bars of 32px with a gap of 16px
  // * plus 8px above first group and below last group
  const height = spacing + barCount * barHeight + barCount * gap;
  return height;
}

export function combineZones(
  rankDir: RankDir,
  viewBy: ViewBy,
  currentZones: Zones["current"],
  compareZones: Zones["compare"]
) {
  const combined: { [key: string]: any } = {};
  // go through the current data, to populate both
  // current and compare object based on the current equip keys
  Object.entries(currentZones.zones).forEach(([key, val]) => {
    combined[key] = {
      current: val,
      compare:
        compareZones && compareZones.zones[key]
          ? compareZones.zones[key]
          : null,
    };
  });

  if (compareZones) {
    // go through compare data to see if there is data that
    // exists in the compare data but not in the current data
    // then we assign null to the current under the equip key object
    // and populate the compare object
    Object.entries(compareZones.zones).forEach(([key, val]) => {
      if (!combined[key]) {
        combined[key] = {
          current: null,
          compare: val,
        };
      }
    });
  }

  const sorted = Object.values(combined).sort((a, b) => {
    if (a.current === null || b.current === null) {
      return 1;
    }
    if (rankDir === "worst") {
      return a.current[viewBy] < b.current[viewBy] ? 1 : -1;
    }

    return a.current[viewBy] < b.current[viewBy] ? -1 : 1;
  });

  return sorted;
}

export function calculateYAxisMaxValue(
  currentZones: Zones["current"],
  compareZones: Zones["compare"]
) {
  let yAxisMaxValue: number;
  const currentDataArray = Object.values(currentZones.zones).filter(
    (d) => d.pctDiscomfort
  );
  const compareDataArray = compareZones
    ? Object.values(compareZones.zones).filter((d) => d.pctDiscomfort)
    : [];

  // get the max hoursDiscomfort value from current period dataset
  const currentDataYAxisMaxValue = Math.max.apply(
    Math,
    currentDataArray.map((z) => z.hoursDiscomfort)
  );
  // get the max hoursDiscomfort value from compared period dataset if there is compare data
  const comparisonDataYAxisMaxValue =
    compareDataArray.length !== 0
      ? Math.max.apply(
          Math,
          compareDataArray.map((z) => z.hoursDiscomfort)
        )
      : null;

  if (comparisonDataYAxisMaxValue === null) {
    yAxisMaxValue = currentDataYAxisMaxValue;
  } else {
    yAxisMaxValue =
      currentDataYAxisMaxValue > comparisonDataYAxisMaxValue
        ? currentDataYAxisMaxValue
        : comparisonDataYAxisMaxValue;
  }

  return yAxisMaxValue;
}

export function createLegendItemsUsingDates(from: string, to: string): string {
  return `${new Date(from).toLocaleString("default", {
    month: "short",
  })} ${new Date(from).getFullYear()} - ${new Date(to).toLocaleString(
    "default",
    { month: "short" }
  )} ${new Date(to).getFullYear()}`;
}

export const getCompareTolist = (i18n: I18n, sliceCount?: number) => {
  const list = [
    {
      value: CompareTo.PreviousPeriod,
      label: i18n._(t("time.previous_period")`Previous Period`),
    },
    {
      value: CompareTo.SamePeriodLastYear,
      label: i18n._(
        t("time.same_period_previous_year")`Same Period Previous Year`
      ),
    },
  ];

  return sliceCount ? list.slice(0, sliceCount) : list;
};

/**
 * Get comparison time frame ranges for seasonal periods.
 *
 * ie. The last X months, compared to the same X months Y years ago.
 */
export function getTimeFrameCompareRanges(
  timeFrameOption: TimeFrame,
  compareToOption: CompareTo,
  today = new Date()
) {
  // today - X month, start of month
  const currentFrom = new Date(today);
  currentFrom.setMonth(currentFrom.getMonth() - timeFrameOption);
  currentFrom.setDate(1);

  // end of last month
  const currentTo = new Date(today);
  currentTo.setDate(0);

  let compareFrom = new Date(today);
  let compareTo = new Date(today);

  if (compareToOption !== CompareTo.PreviousPeriod) {
    // same from date, but Y years ago
    compareFrom = new Date(currentFrom);
    compareFrom.setFullYear(compareFrom.getFullYear() - compareToOption);

    // add on the same period that was taken from currentFrom, less 1 month
    // because months are zero-indexed
    compareTo = new Date(compareFrom);
    compareTo.setMonth(compareTo.getMonth() + (timeFrameOption - 1));

    // then to the end of that month by getting the number of days in that month
    // NOTE: This works by abusing the way `0` works when creating a date
    const daysInMonth = new Date(
      compareTo.getFullYear(),
      compareTo.getMonth() + 1,
      0
    ).getDate();
    compareTo.setDate(daysInMonth);
  } else {
    // the start of the previous period is the last day of the month prior to the currentFrom
    compareTo = new Date(currentFrom);
    compareTo.setDate(0);

    // the end of the previous period comparison is the same difference between the current period
    compareFrom = new Date(compareTo);
    compareFrom.setMonth(compareFrom.getMonth() - (timeFrameOption - 1));
    compareFrom.setDate(1);
  }

  return {
    timeFrameOption,
    compareToOption,
    current: {
      from: currentFrom.toISOString(),
      to: currentTo.toISOString(),
    },
    compare: {
      from: compareFrom.toISOString(),
      to: compareTo.toISOString(),
    },
  };
}
